import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  // useBusinessNoticeSelectList,
  useSendBusinessNotice,
  useEditSentBusinessNotice,
} from "~/services/business/notify";
import { getToday } from "%/date";
import useOpenModal from "^/common/useOpenModal";
import { restoreReservationTime } from "^/business/notify/reservationTime";
import {
  type ActivityOption,
  type RecipientOption,
  type BusinessNoticeTempDetailResponse,
} from "~/types/common";
import dayjs from "dayjs";

export interface GalleryImage {
  ImgID: number;
  ImgURL: string;
}

export interface FormData {
  recipient: string | string[]; // 다중선택 지원
  animal: string;
  content: string;
  hour: string;
  minute: string;
  program: string;
  activity: string;
  bowel: string;
  meal: string;
  snack: string;
  water: string;
  [key: string]: string | string[] | ActivityOption | null; // 동적 필드를 위한 인덱스 시그니처 (다중선택 지원)
}

const useNotifySendSchedule = (
  initData?: BusinessNoticeTempDetailResponse["data"],
  editMode: boolean = false
) => {
  // const { data: selectList } = useBusinessNoticeSelectList();
  const sendNoticeMutation = useSendBusinessNotice();
  const editNoticeMutation = useEditSentBusinessNotice();
  const [selectListData, setSelectListData] = useState<{
    recipients: RecipientOption[];
    activities: Array<{
      cdActivity: string;
      value: string;
      activityValues: ActivityOption[];
    }>;
  }>({
    recipients: [],
    activities: [],
  });

  // Form 관리
  const formMethods = useForm<FormData>({
    defaultValues: {},
    mode: "onChange",
  });

  const { setValue } = formMethods;

  // 받은 프롬프트 데이터를 기본값으로 설정
  useEffect(() => {
    if (initData) {
      const defaultValues: Partial<FormData> = {
        content: initData.notiContents || "",
        notiTitle: initData.notiTitle || "",
        hour: "",
        minute: "",
      };

      // 수신자 설정 (다중선택 지원)
      if (initData.recipients && initData.recipients.length > 0) {
        const selectedRecipients = initData.recipients.filter(
          (recipient) => recipient.selected
        );
        if (selectedRecipients.length > 0) {
          // 다중선택인 경우 배열로, 단일선택인 경우 문자열로 설정
          defaultValues.recipient =
            selectedRecipients.length === 1
              ? selectedRecipients[0].petSeq.toString()
              : selectedRecipients.map((recipient) =>
                  recipient.petSeq.toString()
                );
        }
      }

      // 예약 발송 시간 복원 — 지난 예약은 복원하지 않고(즉시 발송 기본), 분은 10분 단위로 보정
      const restored = restoreReservationTime(
        initData.dtReservation,
        initData.reservationSendFlag
      );
      if (restored) {
        defaultValues.hour = restored.hour;
        defaultValues.minute = restored.minute;
        setSelectedDates([restored.date]);
        setIsTimeToggle(true);
      }

      // 폼 기본값 설정
      formMethods.reset(defaultValues);

      // 스케줄 데이터 설정
      if (initData.schedules && initData.schedules.length > 0) {
        const scheduleDates = initData.schedules.map(
          (schedule) => new Date(schedule?.dtSchedule || "")
        );
        setSelectedDates(scheduleDates);
      }
    }
  }, [initData, formMethods]);

  // 날짜 상태 관리
  const [date] = useState(getToday());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedImgDate, setSelectedImgDate] = useState<Date | null>(null);

  // 모달 상태 관리
  const calendarModal = useOpenModal();
  const imageModal = useOpenModal();
  const imgDateModal = useOpenModal();

  // 이미지 상태 관리
  const [selectedImages, setSelectedImages] = useState<GalleryImage[]>([]);
  const [modalSelectedImages, setModalSelectedImages] = useState<
    GalleryImage[]
  >([]);

  // 토글 상태
  const [isToggle, setIsToggle] = useState(false);
  const [isTimeToggle, setIsTimeToggle] = useState(false);

  // 현재 시간을 계산하는 함수 (분은 10분 단위 올림)
  const getCurrentTimeWithRoundedMinutes = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    let roundedHour = currentHour;
    let roundedMinute = Math.ceil(currentMinute / 10) * 10;
    if (roundedMinute >= 60) {
      roundedHour = (roundedHour + 1) % 24;
      roundedMinute = 0;
    }
    return {
      hour: roundedHour.toString().padStart(2, "0"),
      minute: roundedMinute.toString().padStart(2, "0"),
    };
  };

  // 발송시간 토글 변경 시 현재 시간 자동 설정
  const handleTimeToggleChange = (checked: boolean) => {
    setIsTimeToggle(checked);
    if (checked) {
      const currentTime = getCurrentTimeWithRoundedMinutes();
      setValue("hour", currentTime.hour);
      setValue("minute", currentTime.minute);
    }
  };

  // 다이얼로그 상태
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: "",
    message: "",
  });

  // selectList 데이터 업데이트
  useEffect(() => {
    if (initData) {
      const newSelectListData = {
        recipients: initData.recipients ?? [],
        activities: initData.activities ?? [],
      };
      setSelectListData(newSelectListData);

      // 동적 activity 필드들의 기본값 설정
      const dynamicDefaults: Record<string, ActivityOption | null> = {};
      newSelectListData.activities.forEach((activity) => {
        dynamicDefaults[activity.cdActivity] = null;
      });

      // 폼 기본값 업데이트 (기존 값 유지하면서 동적 필드 추가)
      const currentValues = formMethods.getValues();
      formMethods.reset({
        ...currentValues,
        ...dynamicDefaults,
      });
    }
  }, [initData, formMethods]);

  // 폼 제출 로직
  const bindSubmit = (data: FormData, isSave: boolean = false) => {
    console.log("폼 데이터:", data);

    // isTimeToggle이 true인데 sendDateTime이 필수인지 확인
    if (isTimeToggle && (!data.hour || !data.minute)) {
      alert("예약 발송을 선택한 경우 발송 시간을 설정해주세요.");
      return;
    }

    // 날짜와 시간 조합 - ISO 8601 형태로 변환
    const sendDate = selectedDates.length > 0 ? selectedDates[0] : new Date();

    // 시간과 분이 유효한지 확인
    const hour = parseInt(data.hour) || 0;
    const minute = parseInt(data.minute) || 0;

    // 유효한 시간 범위인지 확인
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      alert(
        "유효하지 않은 시간입니다. 시간은 0-23, 분은 0-59 사이여야 합니다."
      );
      return;
    }

    let sendDateTime;
    try {
      // dayjs를 사용하여 시간대 문제 해결
      const dateTime = dayjs(sendDate)
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);

      // 생성된 dayjs 객체가 유효한지 확인
      if (!dateTime.isValid()) {
        throw new Error("Invalid date");
      }

      // 로컬 시간을 그대로 사용 (UTC 변환하지 않음)
      const year = dateTime.year();
      const month = String(dateTime.month() + 1).padStart(2, "0");
      const day = String(dateTime.date()).padStart(2, "0");
      const hours = String(dateTime.hour()).padStart(2, "0");
      const minutes = String(dateTime.minute()).padStart(2, "0");
      const seconds = String(dateTime.second()).padStart(2, "0");

      sendDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("날짜/시간 변환 오류:", error);
      alert("날짜/시간 설정에 오류가 발생했습니다. 다시 확인해주세요.");
      return;
    }

    // selectList에서 받아온 데이터 구조를 그대로 유지하면서 사용자가 입력/선택한 값들만 변경
    const updatedSelectList = {
      // 필수 필드들 보장
      bizUserSeq: initData?.bizUserSeq || 0,
      companySeq: initData?.companySeq || 0,
      noticeSeq: initData?.noticeSeq || null,
      notiTitle: typeof data.notiTitle === "string" ? data.notiTitle : null,
      notiContents: typeof data.content === "string" ? data.content : null,
      dtReservation: isTimeToggle ? sendDateTime : null, // isTimeToggle이 false면 null
      cdSender: initData?.cdSender || "SENDER_OWNER",
      cdRecipient: initData?.cdRecipient || "RECIPIENT_PET_OWNER",
      reservationSendFlag: isTimeToggle, // isTimeToggle 값을 reservationSendFlag에 설정
      activityFlag:
        selectListData.activities.some(
          (activity) => data[activity.cdActivity]
        ) || false,
      tempSaveYn: isSave ? "Y" : "N", // 저장하기면 "Y", 보내기면 "N"

      // 스케줄 정보 - 선택된 날짜들로 설정
      schedules: selectedDates.map((date) => {
        try {
          // dayjs를 사용하여 시간대 문제 해결
          const dayjsDate = dayjs(date).startOf("day");

          // dayjs 객체가 유효한지 확인
          if (!dayjsDate.isValid()) {
            throw new Error("Invalid date");
          }

          return {
            scheduleSeq: null,
            notiRecvSeq: null,
            noticeSeq: null,
            cdDayOfWeek: null,
            dtSchedule: `${dayjsDate.year()}-${String(dayjsDate.month() + 1).padStart(2, "0")}-${String(dayjsDate.date()).padStart(2, "0")}T${String(dayjsDate.hour()).padStart(2, "0")}:${String(dayjsDate.minute()).padStart(2, "0")}:${String(dayjsDate.second()).padStart(2, "0")}`,
          };
        } catch (error) {
          console.error("스케줄 날짜 변환 오류:", error);
          return {
            scheduleSeq: null,
            notiRecvSeq: null,
            noticeSeq: null,
            cdDayOfWeek: null,
            dtSchedule: (() => {
              const today = dayjs().startOf("day");
              return `${today.year()}-${String(today.month() + 1).padStart(2, "0")}-${String(today.date()).padStart(2, "0")}T00:00:00`;
            })(), // 기본값으로 오늘 자정 사용
          };
        }
      }),

      // 일정 공지 타입의 selected를 true로 변경
      noticeTypes:
        initData?.noticeTypes?.map((noticeType) => ({
          ...noticeType,
          selected: noticeType.cdNotiType === "NOTI_SCHEDULE", // 일정 공지만 true로 설정
        })) || [],

      // 선택된 반려견의 selected를 true로 변경
      recipients:
        initData?.recipients?.map((recipient) => {
          const petSeqStr = recipient.petSeq.toString();
          // data.recipient가 배열인 경우와 단일 값인 경우 모두 처리
          const isSelected = Array.isArray(data.recipient)
            ? data.recipient.includes(petSeqStr)
            : petSeqStr === data.recipient;

          return {
            ...recipient,
            selected: isSelected,
          };
        }) || [],

      // 선택된 활동들의 selected를 true로 변경
      activities:
        initData?.activities?.map((activity) => {
          const userSelectedValue = data[activity.cdActivity];
          if (userSelectedValue && typeof userSelectedValue === "object") {
            return {
              ...activity,
              activityValues: activity.activityValues.map((option) => ({
                ...option,
                selected:
                  option.cdActivityValue ===
                  (userSelectedValue as ActivityOption).cdActivityValue,
              })),
            };
          }
          return activity;
        }) || [],

      // 선택된 이미지들
      photos: selectedImages.map((img) => ({
        notiPhotoSeq: null,
        fileSeq: img.ImgID,
        fileUrl: img.ImgURL,
      })),
    };

    console.log("전송 요청 데이터:", updatedSelectList);

    // 수정 모드 — 발송된 공지의 내용/사진/일정만 갱신한다(푸시 재발송 없음).
    if (editMode) {
      const editPayload = {
        ...updatedSelectList,
        // 사용자가 지운 경우에도 원본 제목으로 복구 (제목 비워짐 방지)
        notiTitle: updatedSelectList.notiTitle ?? initData?.notiTitle ?? null,
        tempSaveYn: "N",
        reservationSendFlag: false,
        dtReservation: null,
        recipients: initData?.recipients ?? updatedSelectList.recipients,
      };

      editNoticeMutation.mutate(editPayload, {
        onSuccess: (response) => {
          setSuccessMessage(
            response.statusCode === 200
              ? { title: "수정 완료", message: "일정 공지가 수정되었습니다." }
              : {
                  title: "수정 실패",
                  message: response.message || "수정에 실패했습니다.",
                }
          );
          setShowSuccessDialog(true);
        },
        onError: (error) => {
          console.error("수정 실패:", error);
          alert("일정 공지 수정에 실패했습니다. 다시 시도해주세요.");
        },
      });
      return;
    }

    // API 호출
    sendNoticeMutation.mutate(updatedSelectList, {
      onSuccess: (response) => {
        console.log(isSave ? "저장 성공:" : "발송 성공:", response);
        const message = isSave
          ? "알림이 성공적으로 저장되었습니다."
          : "알림이 성공적으로 발송되었습니다.";
        let title = isSave ? "알림 저장 완료" : "알림 발송 완료";
        if (response.statusCode === 200) {
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true); // 성공 다이얼로그 표시
        } else {
          title = isSave ? "알림 저장 실패" : "알림 발송 실패";
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true);
        }
        // 폼 초기화 (필요시)
        // formMethods.reset();
      },
      onError: (error) => {
        console.error(isSave ? "저장 실패:" : "발송 실패:", error);
        const errorMessage = isSave
          ? "일정 공지 저장에 실패했습니다. 다시 시도해주세요."
          : "일정 공지 발송에 실패했습니다. 다시 시도해주세요.";
        alert(errorMessage);
      },
    });
  };

  // 모달에서 이미지 선택/해제
  const toggleImageSelection = (img: GalleryImage) => {
    setModalSelectedImages((prev) =>
      prev.find((item) => item.ImgID === img.ImgID)
        ? prev.filter((item) => item.ImgID !== img.ImgID)
        : [...prev, img]
    );
  };

  // 선택된 이미지 미리보기에서 삭제
  const removeSelectedImage = (img: GalleryImage) => {
    setSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
    setModalSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
  };

  // 모달에서 선택한 이미지 확정
  const handleImgModal = () => {
    // 기존 선택된 이미지와 새로 선택한 이미지를 합침
    const merged = [...selectedImages, ...modalSelectedImages];
    // ImgID 기준으로 중복 제거
    const unique = merged.filter(
      (img, idx, arr) => arr.findIndex((i) => i.ImgID === img.ImgID) === idx
    );
    setSelectedImages(unique);
    imageModal.closeModal();
  };

  return {
    // 폼 관련
    formMethods,

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    selectedDates,
    setSelectedDates,
    selectedImgDate,
    setSelectedImgDate,

    // 모달 관련
    calendarModal,
    imageModal,
    imgDateModal,

    // 이미지 관련
    selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    removeSelectedImage,
    handleImgModal,

    // 토글 관련
    isToggle,
    setIsToggle,
    isTimeToggle,
    setIsTimeToggle,
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,
    successMessage,

    // 제출 관련
    bindSubmit,
    isSubmitting: sendNoticeMutation.isPending || editNoticeMutation.isPending,
    submitError: sendNoticeMutation.error ?? editNoticeMutation.error,
    isEditMode: editMode,
  };
};

export default useNotifySendSchedule;
